import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardFilterOption,
  AdminDashboardFilterOptions,
  AdminDashboardFilters,
  AdminDashboardScope
} from '@features/admin-panel/models/admin-dashboard.models';

/** A filter field the bar can set; each maps to one key of the filter options response. */
type AdminDashboardFilterField = keyof AdminDashboardFilters;

/** One pill filter: the field it sets, its label, the options-response key it lists and its test id. */
interface AdminDashboardFilterDef {
  field: AdminDashboardFilterField;
  label: string;
  optionsKey: 'Packages' | 'Sources' | 'LicenceStatuses' | 'Owners';
  testId: string;
}

/** Every filter, in the bar's order. */
const ALL_FILTERS: AdminDashboardFilterDef[] = [
  {field: 'packages', label: 'Solution / package', optionsKey: 'Packages', testId: 'filter-packages'},
  {field: 'sources', label: 'Company source', optionsKey: 'Sources', testId: 'filter-sources'},
  {field: 'licenceStatuses', label: 'Licence status', optionsKey: 'LicenceStatuses', testId: 'filter-licencestatuses'},
  {field: 'owners', label: 'Account owner', optionsKey: 'Owners', testId: 'filter-owners'}
];

/** Licences and account owners live in Client Manager; non-clients have neither. */
const NON_CLIENT_FILTERS: AdminDashboardFilterDef[] =
  ALL_FILTERS.filter(def => def.field === 'packages' || def.field === 'sources');

/** Shared empty arrays, so a template binding never sees a fresh array per check. */
const NO_OPTIONS: AdminDashboardFilterOption[] = [];
const NO_VALUES: string[] = [];

/**
 * The Admin dashboard's header filters: one OCA-E-159 pill trigger per filter, each opening a menu of checkboxes
 * with company counts (ITP-E-148-03). Loads every filter's choices in ONE `getFilterOptions` call per scope/period
 * change and emits the new filters on every pick; a field left empty is omitted, so clearing a filter restores the
 * unfiltered totals. Before the first daily snapshot it shows a hint instead.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-filter-bar',
  templateUrl: './admin-dashboard-filter-bar.component.html',
  styleUrls: ['./admin-dashboard-filter-bar.component.scss']
})
export class AdminDashboardFilterBarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() period: string;
  @Input() value: AdminDashboardFilters = {};
  @Output() valueChange = new EventEmitter<AdminDashboardFilters>();
  /** The companies in the scope before any filter, after each options load that carries it (ITP-E-162-05). */
  @Output() scopeTotal = new EventEmitter<number>();

  options: AdminDashboardFilterOptions | null = null;

  private optionsSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService) {}

  ngOnInit(): void {
    this.loadOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const scopeOrPeriod = [changes['scope'], changes['period']].filter(change => !!change);
    if (scopeOrPeriod.length && scopeOrPeriod.every(change => !change.firstChange)) {
      this.loadOptions();
    }
  }

  ngOnDestroy(): void {
    this.optionsSubscription?.unsubscribe();
  }

  /** The filters this scope offers, in the bar's order (one of two constant arrays). */
  get visibleFilters(): AdminDashboardFilterDef[] {
    return this.scope === 'NonClients' ? NON_CLIENT_FILTERS : ALL_FILTERS;
  }

  /** One filter's choices, from the single options response. */
  optionsFor(def: AdminDashboardFilterDef): AdminDashboardFilterOption[] {
    return this.options?.[def.optionsKey] ?? NO_OPTIONS;
  }

  /** The values picked for one field (empty when none). */
  selected(field: AdminDashboardFilterField): string[] {
    return this.value?.[field] ?? NO_VALUES;
  }

  isSelected(field: AdminDashboardFilterField, optionValue: string): boolean {
    return this.selected(field).includes(optionValue);
  }

  /** A checkbox in a filter's menu was clicked: adds or removes that value, then emits. */
  toggle(field: AdminDashboardFilterField, optionValue: string): void {
    const current = this.selected(field);
    this.onChange(field, current.includes(optionValue)
      ? current.filter(value => value !== optionValue)
      : [...current, optionValue]);
  }

  /** True once any filter is on: the bar then offers "Clear filters". */
  get hasFilters(): boolean {
    return ALL_FILTERS.some(def => this.selected(def.field).length > 0);
  }

  /** "Clear filters": every filter off at once (emits no filters). */
  clear(): void {
    this.value = {};
    this.valueChange.emit({});
  }

  onChange(field: AdminDashboardFilterField, selected: string[]): void {
    const next: AdminDashboardFilters = {...this.value};
    if (selected?.length) {
      next[field] = [...selected];
    } else {
      delete next[field];
    }
    this.value = next;
    this.valueChange.emit(next);
  }

  /** One options call per scope / period; its ScopeTotal goes up to the page for "X of Y" (ITP-E-162-05). */
  private loadOptions(): void {
    this.optionsSubscription?.unsubscribe();
    this.optionsSubscription = this.dataService.getFilterOptions({scope: this.scope, period: this.period}).pipe(
      catchError(() => of(null))
    ).subscribe(options => {
      this.options = options;
      if (typeof options?.ScopeTotal === 'number') {
        this.scopeTotal.emit(options.ScopeTotal);
      }
    });
  }
}
